// Relative imports (not `@/`) so bun can test this module.
import { getHexTileMapKey } from '../hexmap/model/keys';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import { type Cell, firingFor, hitChance, type Shot, type Unit } from '../sim';

/** Hexes between two cells. */
export function hexDistance(a: Cell, b: Cell): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

/** The cube-rounded cell at axial (q, r). */
function roundCell(q: number, r: number): Cell {
  const s = -q - r;
  let rq = Math.round(q);
  let rr = Math.round(r);
  const rs = Math.round(s);
  const dq = Math.abs(rq - q);
  const dr = Math.abs(rr - r);
  const ds = Math.abs(rs - s);
  if (dq > dr && dq > ds) rq = -rr - rs;
  else if (dr > ds) rr = -rq - rs;
  return { q: rq, r: rr };
}

/** The cells on the straight line from `a` to `b`, both included (distance + 1 of them). */
export function hexLine(a: Cell, b: Cell): Cell[] {
  const n = hexDistance(a, b);
  const line: Cell[] = [];
  // The nudge keeps points exactly on a hex edge from rounding both ways.
  for (let i = 0; i <= n; i++) {
    const t = n === 0 ? 0 : i / n;
    line.push(roundCell(a.q + (b.q - a.q) * t + 1e-6, a.r + (b.r - a.r) * t + 2e-6));
  }
  return line;
}

/**
 * The part of a shot's flight (fractions 0–1 from `from` to `to`) the viewer
 * may see, or null for none. A shot with both ends in sight is drawn whole;
 * one fired from out of sight appears where the line enters sight (the last
 * stretch of visible cells before `to`), so the shooter's tile stays hidden;
 * one landing out of sight vanishes where the line leaves sight and has no
 * impact drawn (`t1` < 1). Neither end in sight: nothing.
 */
export function visibleSpan(
  from: Cell,
  to: Cell,
  isVisible: (key: HexTileMapKey) => boolean
): { t0: number; t1: number } | null {
  const fromSeen = isVisible(getHexTileMapKey(from));
  const toSeen = isVisible(getHexTileMapKey(to));
  if (fromSeen && toSeen) return { t0: 0, t1: 1 };
  if (!fromSeen && !toSeen) return null;
  const line = hexLine(from, to);
  const n = line.length - 1;
  if (toSeen) {
    // First cell of the visible run ending at `to`; appear at its near edge.
    let j = n;
    while (j > 0 && isVisible(getHexTileMapKey(line[j - 1]))) j--;
    return { t0: (j - 0.5) / n, t1: 1 };
  }
  // Last cell of the visible run starting at `from`; vanish at its far edge.
  let j = 0;
  while (j < n && isVisible(getHexTileMapKey(line[j + 1]))) j++;
  return { t0: 0, t1: (j + 0.5) / n };
}

/**
 * Display tick (same units as `SimState.tick`) at which a shot is `t` of the
 * way along its flight (0 at firing, 1 at impact). A shot fired during the
 * tick numbered `fireTick` is first in the state stamped `fireTick + 1`, and
 * its damage lands during the tick numbered `impactTick`, so the flight runs
 * from `fireTick + 1` to `impactTick + 1`.
 */
export function shotTick(shot: Pick<Shot, 'fireTick' | 'impactTick'>, t: number): number {
  return shot.fireTick + 1 + t * Math.max(1, shot.impactTick - shot.fireTick);
}

/**
 * How far the display clock aims to run behind the sim's own clock, in ticks.
 * States arrive once a tick, so the latest one is on average half a tick old
 * and the sim's clock reads about `latest + 0.5`.
 */
export const DISPLAY_LAG = 0.5;

/**
 * Advances a smooth display clock (in ticks) by `delta` seconds: it runs at
 * the sim rate, nudged faster or slower (0.5×–1.5×) towards `DISPLAY_LAG`
 * behind the sim's clock, stays between 1.5 ticks behind and 1 tick ahead of
 * the latest state's tick, and snaps back when far off (a stall, an adopted
 * snapshot). Running just behind lets a shot that arrives in a new state fly
 * its whole flight, however unevenly states arrive.
 */
export function advanceDisplayTick(
  display: number,
  latest: number,
  delta: number,
  dt: number
): number {
  const error = latest + 0.5 - DISPLAY_LAG - display;
  if (Math.abs(error) > 3) return latest - DISPLAY_LAG;
  const rate = Math.min(1.5, Math.max(0.5, 1 + 0.5 * error));
  const next = display + (delta / dt) * rate;
  return Math.min(latest + 1, Math.max(latest - 1.5, next));
}

/**
 * A ranged unit's accuracy for its HUD chip: the chance to hit a still target
 * (or a building) at its full range, and a tooltip on closer shots and moving
 * targets (rules.yaml `shots.movingPenalty`). Null for melee and non-fighters.
 */
export function accuracyText(rules: RuleSet, unit: Unit): { label: string; title: string } | null {
  const firing = firingFor(rules, unit);
  if (!firing) return null;
  const range = rules.units[unit.type]?.range ?? 1;
  const pct = (p: number) => Math.round(p * 100);
  const far = pct(hitChance(firing.accuracy, range));
  const near = pct(hitChance(firing.accuracy, 1));
  const penalty = pct(rules.shots.movingPenalty[firing.kind] ?? 0);
  return {
    label: `Hits ~${far}% at ${range} hexes`,
    title:
      `Against still units and buildings: ${near}% at 1 hex, ${far}% at ${range}. ` +
      `Moving units are harder to hit, the faster the harder (up to ${penalty}% less against the fastest).`,
  };
}
